import { useEffect } from 'react';
import { POS_GOLD } from '../config/posRoomStatus';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import type { PortalAccess } from '../contexts/AuthContext';

const STORAGE_KEY = 'sentinel_last_portal';

// Hotel Manager Portal entry removed — module is parked for teardown
// (zero production usage as of 2026-05-13 audit). Restore if the module
// is ever revived. POS routes still exist; the entry just doesn't appear
// in the portal-picker UI.
const portals: { key: keyof PortalAccess; label: string; description: string; path: string; color: string }[] = [
  { key: 'admin', label: 'Operations Portal', description: 'Bookings, invoices, CRM, reports', path: '/', color: '#772cbb' },
  { key: 'ambassador', label: 'Ambassador Portal', description: 'Brand ambassador dashboard & payouts', path: '/ambassador', color: POS_GOLD },
];

export default function PortalSelector() {
  const { getPortalAccess, user } = useAuth();
  const navigate = useNavigate();
  const access = getPortalAccess();

  const available = portals.filter(p => access[p.key]);

  // Auto-redirect if only one portal available
  useEffect(() => {
    if (available.length === 1) {
      localStorage.setItem(STORAGE_KEY, available[0]!.key);
      navigate(available[0]!.path, { replace: true });
    }
  }, [available, navigate]);

  if (available.length <= 1) return null;

  const handleSelect = (portal: typeof portals[number]) => {
    localStorage.setItem(STORAGE_KEY, portal.key);
    navigate(portal.path, { replace: true });
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f5f5f5',
      padding: '24px',
    }}>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h1 style={{ fontSize: 'var(--text-4xl)', fontWeight: 700, color: '#1a1a1a', margin: 0 }}>
          Welcome, {user?.first_name || 'User'}
        </h1>
        <p style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)', marginTop: '8px' }}>
          Select a portal to continue
        </p>
      </div>

      <div style={{
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        justifyContent: 'center',
        maxWidth: '720px',
      }}>
        {available.map(portal => (
          <button
            key={portal.key}
            onClick={() => handleSelect(portal)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '200px',
              padding: '32px 24px',
              borderRadius: 'var(--radius-lg)',
              border: '2px solid #e0e0e0',
              background: 'var(--color-white)',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
            onMouseOver={e => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = portal.color;
              (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 4px 12px ${portal.color}33`;
            }}
            onMouseOut={e => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = '#e0e0e0';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
            }}
          >
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: portal.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-white)',
              fontSize: 'var(--text-3xl)',
              fontWeight: 700,
              marginBottom: '12px',
            }}>
              {portal.label[0]}
            </div>
            <div style={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: '#1a1a1a' }}>
              {portal.label}
            </div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', marginTop: '6px', textAlign: 'center' }}>
              {portal.description}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
